import React, { useMemo, forwardRef } from 'react';
import { times, update, pipe } from 'ramda';
import { makeStyles } from 'tss-react/mui';
import { Theme, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { EDBreadcrumbs } from '../../../../../BaseComponents/Breadcrumbs/EDBreadcrumbs';
import EDPaper from '../../../../../BaseComponents/Paper/EDPaper';
import { curriedPadArrayEnd } from '../../../../../Utils/utils';
import {
  AvenyThemeDark,
  BREADCRUMB_BAR_HEIGHT_PX,
} from '../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import { useIsBreakpointOrSmaller } from '../../../../../Components/BreakpointContext/BreakpointContextUtils';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import { HEADER, HeaderReferences, SEPARATOR } from './BreadcrumbsContext';
import { useIsFloatingMenuVisible } from '../../../../../Components/FloatingBottomArea/FloatingMenu';

interface IProps {
  headerRefs: HeaderReferences;
  setBackbuttonRefs: React.Dispatch<
    React.SetStateAction<Array<HTMLSpanElement | null>>
  >;
  setStepperRefs: React.Dispatch<
    React.SetStateAction<Array<HTMLDivElement | null>>
  >;
  setMenuRef: React.Dispatch<React.SetStateAction<HTMLSpanElement | null>>;
  topIndex: number | null;
}

const useStyles = makeStyles()((theme: Theme) => ({
  functionMenu: {
    marginRight: theme.spacing(1),
  },
  container: {
    zIndex: 5000,
    width: 'calc(100% - 48px)',
    height: BREADCRUMB_BAR_HEIGHT_PX,
    background: theme.palette.secondary.dark,
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    overflow: 'hidden',
    flexGrow: 2,
  },
  ol: {
    width: '100%',
    marginLeft: 4,
  },
  li: {
    display: 'block' /* Makes the <li> element a block-level element */,
    listStyleType: 'none' /* Removes the default list item marker */,
    margin: 0 /* Removes default margin */,
    padding: 0 /* Removes default padding */,
  },
  breadcrumbsBackground: {
    display: 'flex',
    paddingLeft: 4,
    height: BREADCRUMB_BAR_HEIGHT_PX,
    zIndex: 1101,
    background: theme.palette.secondary.dark,
    color: theme.palette.common.white,
    alignItems: 'center',
    boxShadow: theme.shadows[3],
  },
}));

interface IBackButtonAreaProps {
  ref: React.Ref<HTMLSpanElement>;
  navlevel: number;
}

/** en <span> som vi portalar in bakåtknapp till */
const BackButtonArea: React.FC<IBackButtonAreaProps> = forwardRef<
  HTMLSpanElement,
  IBackButtonAreaProps
>((props, ref) => {
  return (
    <EDBox
      sx={{
        position: 'absolute',
        zIndex: props.navlevel,
      }}
      ref={ref}
    />
  );
});

const maxDetailDepth = 20;

interface IBackButtonRefsProps {
  setBackbuttonRefs: React.Dispatch<
    React.SetStateAction<Array<HTMLSpanElement | null>>
  >;
}

export const RutinBackButtonRefs: React.FC<IBackButtonRefsProps> = ({
  setBackbuttonRefs,
}) => {
  const items = useMemo(
    () =>
      times((index: number) => {
        return (
          <BackButtonArea
            key={index}
            navlevel={index}
            ref={ref => {
              setBackbuttonRefs(oldrefs =>
                pipe(
                  curriedPadArrayEnd<HTMLSpanElement | null>(null)(index + 1),
                  update(index, ref)
                )(oldrefs)
              );
            }}
          />
        );
      }, maxDetailDepth),
    [setBackbuttonRefs]
  );

  return <>{items}</>;
};

const RutinBreadcrumbsBase: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { setBackbuttonRefs, setStepperRefs, setMenuRef } = props;
  const { classes, cx } = useStyles();
  const isSm = useIsBreakpointOrSmaller('sm');
  const floatingTouchLayout = useIsFloatingMenuVisible();
  const stepperRefs = useMemo(
    () =>
      times((index: number) => {
        return (
          <div
            key={index}
            ref={ref => {
              setStepperRefs(oldrefs =>
                pipe(
                  curriedPadArrayEnd<HTMLDivElement | null>(null)(index + 1),
                  update(index, ref)
                )(oldrefs)
              );
            }}
          />
        );
      }, maxDetailDepth),
    [setStepperRefs]
  );
  // const topIndex = props.topIndex ?? -1;
  return (
    <EDPaper
      className={classes.breadcrumbsBackground}
      testid="RutinBreadcrumbs"
    >
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={AvenyThemeDark}>
          {!floatingTouchLayout && (
            <EDBox
              sx={{ width: 48, height: BREADCRUMB_BAR_HEIGHT_PX, minWidth: 48 }}
            >
              <RutinBackButtonRefs setBackbuttonRefs={setBackbuttonRefs} />
            </EDBox>
          )}
          <EDBreadcrumbs
            activeItems={props.topIndex ? props.topIndex + 1 : 0}
            itemsBeforeCollapse={isSm ? 1 : undefined}
            itemsAfterCollapse={isSm ? 1 : undefined}
            maxItems={isSm ? 2 : undefined}
            collapsedIconDisabled={isSm}
            classes={{
              root: cx(classes.container),
              ol: classes.ol,
            }}
          >
            <li className={classes.li} ref={props.headerRefs[0][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[0][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[1][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[1][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[2][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[2][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[3][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[3][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[4][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[4][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[5][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[5][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[6][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[6][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[7][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[7][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[8][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[8][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[9][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[9][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[10][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[10][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[11][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[11][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[12][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[12][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[13][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[13][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[14][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[14][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[15][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[15][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[16][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[16][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[17][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[17][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[18][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[18][HEADER]} />
            <li className={classes.li} ref={props.headerRefs[19][SEPARATOR]} />
            <li className={classes.li} ref={props.headerRefs[19][HEADER]} />
          </EDBreadcrumbs>
          <div>{stepperRefs}</div>
        </ThemeProvider>
      </StyledEngineProvider>
      <div ref={setMenuRef} className={classes.functionMenu} />
    </EDPaper>
  );
};

export const RutinBreadcrumbs = RutinBreadcrumbsBase;
